(function () {
  'use strict';

  const STORAGE_KEY = 'rd:compare:v1';
  const RETURN_URL_KEY = 'rd:compare:return-url';
  const MAX_ITEMS = 4;
  const MIN_ITEMS = 2;

  function readState() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));

      if (parsed && Array.isArray(parsed.items)) {
        return {
          items: parsed.items
            .filter((item) => item && item.handle && item.variant)
            .slice(0, MAX_ITEMS),
          context: typeof parsed.context === 'string' ? parsed.context : ''
        };
      }
    } catch (error) {
      /* corrupted state falls back to empty */
    }

    return { items: [], context: '' };
  }

  function readItems() {
    return readState().items;
  }

  function itemKey(item) {
    if (item.colorId) {
      return item.handle + '|c' + item.colorId;
    }

    if (item.color) {
      return item.handle + '|' + item.color;
    }

    return item.handle + '|v' + item.variant;
  }

  // context = where the selection was made (a collection handle or 'finder').
  // Omitted -> the stored context is preserved.
  function writeItems(items, context) {
    const deduped = [];

    items.forEach((item) => {
      if (item && item.handle && item.variant && !deduped.some((existing) => itemKey(existing) === itemKey(item))) {
        deduped.push(item);
      }
    });

    const nextContext = context === undefined ? readState().context : context;

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: deduped.slice(0, MAX_ITEMS), context: nextContext }));
    } catch (error) {
      /* storage unavailable (private mode) — UI keeps working for this page only */
    }

    document.dispatchEvent(new CustomEvent('rd:compare:change', { detail: { items: readItems() } }));
  }

  const compareState = {
    MAX: MAX_ITEMS,
    MIN: MIN_ITEMS,
    RETURN_URL_KEY: RETURN_URL_KEY,
    getItems: readItems,
    setItems: writeItems,
    key: itemKey,

    getContext() {
      return readState().context;
    },

    setContext(context) {
      writeItems(readItems(), context);
    },

    has(key) {
      return readItems().some((item) => itemKey(item) === key);
    },

    isFull() {
      return readItems().length >= MAX_ITEMS;
    },

    add(item) {
      const items = readItems();

      if (items.some((existing) => itemKey(existing) === itemKey(item))) {
        return true;
      }

      if (items.length >= MAX_ITEMS) {
        return false;
      }

      items.push(item);
      writeItems(items);
      return true;
    },

    remove(key) {
      writeItems(readItems().filter((item) => itemKey(item) !== key));
    },

    refresh(item) {
      const items = readItems();
      const existing = items.find((candidate) => itemKey(candidate) === itemKey(item));

      if (!existing) {
        return;
      }

      const changed = ['variant', 'color', 'title', 'url', 'image', 'price'].some((field) => item[field] && item[field] !== existing[field]);

      if (changed) {
        Object.assign(existing, item);
        writeItems(items);
      }
    },

    clear() {
      writeItems([]);
    },

    getReturnUrl(fallback) {
      try {
        return window.sessionStorage.getItem(RETURN_URL_KEY) || fallback;
      } catch (error) {
        return fallback;
      }
    },

    setReturnUrl(url) {
      try {
        window.sessionStorage.setItem(RETURN_URL_KEY, url);
      } catch (error) {
        /* ignore */
      }
    }
  };

  window.themeVariables = window.themeVariables || {};
  window.themeVariables.compareState = window.themeVariables.compareState || compareState;
})();

(function () {
  'use strict';

  if (window.customElements.get('compare-toggle')) {
    return;
  }

  class CompareToggle extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this._input = this.querySelector('input[type="checkbox"]');
      this._tooltip = this.querySelector('.compare-toggle__tooltip');

      if (!this._input) {
        return;
      }

      const signal = this._abortController.signal;

      this._input.addEventListener('change', () => this._onChange(), { signal });
      document.addEventListener('rd:compare:change', () => this._sync(true), { signal });

      this._sync();
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
      }

      clearTimeout(this._tooltipTimer);
    }

    get _state() {
      return window.themeVariables.compareState;
    }

    get _key() {
      return this._state.key({
        handle: this.dataset.handle,
        variant: this.dataset.variant,
        color: this.dataset.color,
        colorId: this.dataset.colorId
      });
    }

    _onChange() {
      if (this._input.checked) {
        const added = this._state.add({
          handle: this.dataset.handle,
          variant: this.dataset.variant,
          color: this.dataset.color,
          colorId: this.dataset.colorId,
          title: this.dataset.title,
          url: this.dataset.url,
          image: this.dataset.image,
          price: this.dataset.price
        });

        if (!added) {
          this._input.checked = false;
          this._showTooltip();
        }
      } else {
        this._state.remove(this._key);
      }
    }

    _sync(fromEvent) {
      const selected = this._state.has(this._key);
      const atLimit = !selected && this._state.isFull();

      this._input.checked = selected;
      this._input.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
      this.classList.toggle('compare-toggle--limit', atLimit);

      // Re-localise the stored snapshot from this card's (current-locale) dataset, but only
      // on connect — never while handling a change event, to avoid re-entrant dispatch.
      if (selected && !fromEvent) {
        this._state.refresh({
          handle: this.dataset.handle,
          variant: this.dataset.variant,
          color: this.dataset.color,
          colorId: this.dataset.colorId,
          title: this.dataset.title,
          url: this.dataset.url,
          image: this.dataset.image,
          price: this.dataset.price
        });
      }
    }

    _showTooltip() {
      if (!this._tooltip) {
        return;
      }

      this._tooltip.hidden = false;
      clearTimeout(this._tooltipTimer);
      this._tooltipTimer = setTimeout(() => {
        this._tooltip.hidden = true;
      }, 2500);
    }
  }

  window.customElements.define('compare-toggle', CompareToggle);
})();

(function () {
  'use strict';

  if (window.customElements.get('compare-bar')) {
    return;
  }

  class CompareBar extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this._countElement = this.querySelector('.compare-bar__count');
      this._hintElement = this.querySelector('.compare-bar__hint');
      this._ctaElement = this.querySelector('.compare-bar__cta');
      this._toggleButton = this.querySelector('.compare-bar__toggle');
      this._panel = this.querySelector('.compare-bar__panel');
      this._list = this.querySelector('.compare-bar__list');
      this._itemTemplate = this.querySelector('template[data-compare-bar-item]');
      this._expanded = true;

      const signal = this._abortController.signal;

      // The compare selection is scoped to where it was made. A finder-built
      // selection is the exception: keep those products when the customer uses
      // "Decke hinzufügen", even when their cards are absent from this
      // collection. Ordinary cross-collection navigation still starts clean.
      const collectionMatch = window.location.pathname.match(/\/collections\/([^/?]+)/);

      if (collectionMatch) {
        const context = collectionMatch[1];
        const storedContext = this._state.getContext();

        if (storedContext && storedContext !== context && storedContext !== 'finder' && this._state.getItems().length) {
          this._state.clear();
        }

        this._state.setContext(context);
      }

      this._state.setReturnUrl(window.location.pathname + window.location.search);

      this._toggleButton.addEventListener('click', () => {
        this._expanded = !this._expanded;
        this._render();
      }, { signal });

      this.querySelector('.compare-bar__clear').addEventListener('click', () => this._state.clear(), { signal });

      this._list.addEventListener('click', (event) => {
        const removeButton = event.target.closest('.compare-bar__item-remove');

        if (removeButton) {
          this._state.remove(removeButton.closest('.compare-bar__item').dataset.key);
        }
      }, { signal });

      this._ctaElement.addEventListener('click', (event) => {
        if (this._ctaElement.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
        }
      }, { signal });

      document.addEventListener('rd:compare:change', () => this._render(), { signal });

      this._render();
    }

    disconnectedCallback() {
      if (this._abortController) {
        this._abortController.abort();
      }
    }

    get _state() {
      return window.themeVariables.compareState;
    }

    _render() {
      const items = this._state.getItems();

      this.hidden = items.length === 0;

      if (this.hidden) {
        return;
      }

      this._countElement.textContent = (this.dataset.countTemplate || '[[count]] / [[max]]')
        .replace('[[count]]', items.length)
        .replace('[[max]]', this._state.MAX);

      const meetsMinimum = items.length >= this._state.MIN;

      this._hintElement.textContent = meetsMinimum ? (this.dataset.rangeHint || '') : (this.dataset.minHint || '');
      this._hintElement.hidden = false;

      // Only expose a real href once the minimum is met — otherwise middle-click /
      // open-in-new-tab (which don't fire a cancelable click) could bypass the gate.
      if (meetsMinimum) {
        const productsParam = items.map((item) => item.handle + ':' + item.variant).join(',');
        // view=produktvergleich forces the comparison template while the page's template suffix
        // is not assignable in the admin (the template only exists on the unpublished theme).
        this._ctaElement.setAttribute('href', this.dataset.compareUrl + '?products=' + encodeURIComponent(productsParam) + '&view=produktvergleich');
      } else {
        this._ctaElement.removeAttribute('href');
      }

      this._ctaElement.setAttribute('aria-disabled', meetsMinimum ? 'false' : 'true');

      this._panel.hidden = !this._expanded;
      this._toggleButton.setAttribute('aria-expanded', this._expanded ? 'true' : 'false');

      this._list.replaceChildren(...items.map((item) => this._renderItem(item)));
    }

    _renderItem(item) {
      const fragment = this._itemTemplate.content.cloneNode(true);
      const listItem = fragment.querySelector('.compare-bar__item');
      const image = fragment.querySelector('.compare-bar__item-image');

      listItem.dataset.key = this._state.key(item);

      if (item.image) {
        image.src = item.image;
        image.alt = item.title || '';
      } else {
        image.remove();
      }

      fragment.querySelector('.compare-bar__item-title').textContent = item.title || '';
      fragment.querySelector('.compare-bar__item-price').textContent = item.price || '';

      return listItem;
    }
  }

  window.customElements.define('compare-bar', CompareBar);
})();
